import {
  CIAM_AUTH_MAX_RETRIES,
  SessionStorageKeys,
  LocalStorageKeys,
  TOKEN_REFRESHING_MAX_ATTEMPTS,
} from '@commons/consts';
import { getSubRouteName } from '@routers/utils';
import { v4 } from 'uuid';
import isFinite from 'lodash/isFinite';

export const createStateParam = (path: string) => `${v4()}${path}`;

// Decodes the JWT payload locally (no network call) to check the `exp`
// claim, so an already-expired token can be treated as absent before it
// triggers a doomed round of API calls. Returns false - "not known to be
// expired" - for anything that isn't a decodable JWT with an `exp` claim,
// so unrecognized token formats keep behaving exactly as before.
export const isTokenExpired = (token: string): boolean => {
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) {
      return false;
    }

    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));

    if (typeof payload.exp !== 'number') {
      return false;
    }

    return Date.now() >= payload.exp * 1000;
  } catch {
    return false;
  }
};

export const getPathFromState = (state: string) => {
  const startIndex = state.lastIndexOf('/');
  const path = state.substring(startIndex);

  return getSubRouteName(path) ? path : undefined;
};

export const shouldRefreshToken = (): boolean => {
  const refreshAttempts = sessionStorage.getItem(
    SessionStorageKeys.tokenRefreshingAttempts,
  );

  if (refreshAttempts && +refreshAttempts >= TOKEN_REFRESHING_MAX_ATTEMPTS) {
    return false;
  }

  if (refreshAttempts && isFinite(+refreshAttempts)) {
    const increasedAttemptsAmount = +refreshAttempts + 1;

    sessionStorage.setItem(
      SessionStorageKeys.tokenRefreshingAttempts,
      increasedAttemptsAmount.toString(),
    );
  } else {
    sessionStorage.setItem(SessionStorageKeys.tokenRefreshingAttempts, '1');
  }

  return true;
};

export const shouldRetryAuth = (): boolean => {
  const retriesAmount = localStorage.getItem(LocalStorageKeys.ciamAuthRetries);

  if (retriesAmount && +retriesAmount >= CIAM_AUTH_MAX_RETRIES) {
    localStorage.removeItem(LocalStorageKeys.ciamAuthRetries);
    return false;
  }

  if (retriesAmount && isFinite(+retriesAmount)) {
    const increasedRetriesAmount = +retriesAmount + 1;

    localStorage.setItem(
      LocalStorageKeys.ciamAuthRetries,
      increasedRetriesAmount.toString(),
    );
  } else {
    localStorage.setItem(LocalStorageKeys.ciamAuthRetries, '1');
  }

  return true;
};
